import { motion, useReducedMotion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { pickByLanguage } from "@/lib/language";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { landingFaqs } from "@/components/landing/faqData";
import { Eyebrow } from "@/components/landing/_primitives";

export function FAQSection() {
  const { language } = useLanguage();
  const rm = useReducedMotion();
  const location = useLocation();
  const isPricingPage = location.pathname === "/pricing";
  const visibleFaqs = isPricingPage ? landingFaqs : landingFaqs.slice(0, 4);

  return (
    <section id="faq" className="relative overflow-hidden bg-[#fffaf5] py-14 sm:py-20 lg:py-24">
      {/* Calm ambient layer — token-driven */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -right-24 top-12 h-[420px] w-[420px] rounded-full bg-[#ffd9b8]/30 blur-[140px]" />
      </div>

      <div className="container">
        <div className="grid gap-8 lg:gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <motion.div
            initial={rm ? false : { opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: rm ? 0 : 0.45 }}
            className="max-w-xl"
          >
            <Eyebrow withDot accent="15 90% 58%" className="text-[#9a7258]">
              {pickByLanguage(language, { pt: "FAQ", en: "FAQ" })}
            </Eyebrow>
            <h2 className="mt-4 font-editorial font-semibold text-[clamp(2rem,6vw,4rem)] leading-[1.05] sm:leading-[1.02] tracking-[-0.035em] text-[#201814]">
              {pickByLanguage(language, {
                pt: "Perguntas que aparecem antes da compra.",
                en: "Questions that show up before the purchase.",
              })}
            </h2>
            <p className="mt-5 text-[15px] leading-7 text-[#66584d] sm:text-lg sm:leading-8">
              {pickByLanguage(language, {
                pt: "A parte importante da landing não é só parecer boa. Ela precisa remover atrito, explicar o produto e deixar a próxima ação óbvia.",
                en: "The important part of a landing page is not just looking good. It needs to remove friction, explain the product, and make the next action obvious.",
              })}
            </p>
            <p className="mt-8 text-sm leading-7 text-[#76675b]">
              {pickByLanguage(language, {
                pt: "Ainda tem algo específico em mente?",
                en: "Still have something specific in mind?",
              })}{" "}
              <Link to="/contact" className="font-semibold text-[#ef7b43] underline decoration-[#ef7b43]/30 underline-offset-4 transition-colors hover:decoration-[#ef7b43]">
                {pickByLanguage(language, { pt: "Fale com a gente", en: "Talk to us" })}
              </Link>
              .
            </p>
          </motion.div>

          <motion.div
            initial={rm ? false : { opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: rm ? 0 : 0.45, delay: 0.08 }}
            className="relative overflow-hidden rounded-[32px] border border-[#eadbcc] bg-white p-3 shadow-[0_24px_60px_rgba(111,79,52,0.08)]"
          >
            {/* Top hairline — primary accent for editorial premium feel */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 h-px"
              style={{
                background:
                  "linear-gradient(90deg, transparent, rgba(239,123,67,0.55), transparent)",
              }}
            />
            <Accordion type="single" collapsible className="space-y-3">
              {visibleFaqs.map((faq, index) => (
                <AccordionItem
                  key={faq.question.en}
                  value={`faq-${index}`}
                  className="rounded-[24px] border border-[#f0e3d7] bg-[#fffaf5] px-5 transition-colors data-[state=open]:bg-white data-[state=open]:border-[#eadbcc]"
                >
                  <AccordionTrigger className="py-5 text-left text-[1rem] font-medium leading-7 text-[#241b17] hover:no-underline">
                    {pickByLanguage(language, faq.question)}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-sm leading-7 text-[#63564b]">
                    {pickByLanguage(language, faq.answer)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
